import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  extend,
  number as numberType,
  params,
  shape as shapeType,
  arrayOf as arrayOfType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { Price } from '@thd-olt-component-react/price';
import { Link } from '@one-thd/sui-atomic-components';
import {
  ProductPod,
  PodSticker,
  ProductATC,
  ProductRating,
  ProductBadge,
  ProductImage,
  PodFooter,
  PodSection,
  PodSpacer,
} from '@thd-olt-component-react/product-pod';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { ProductServiceAddOns } from '../ProductServiceAddOns';
import { SeeMoreOptions } from './SeeMoreOptions';
import { sendDataToNewRelic } from '../../../newRelicLoggingUtils';

export const SpecialBuyPromosProductPod = ({ itemId, showPlaceholder, position }) => {
  const { storeId, storeZip } = useStore();
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const theme = useTheme(SpecialBuyPromosProductPod);
  const { hideAddToList, hidePickupFulfillment } = theme.props;

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId,
      zipCode: storeZip,
    },
    skip: showPlaceholder,
  });

  const collectionName = data?.product?.details?.collection?.name;
  const collectionUrl = data?.product?.details?.collection?.url;
  const showShopThisCollectionLink = collectionName && collectionUrl;
  const canonicalUrl = data?.product?.identifiers?.canonicalUrl;
  const totalNumberOfOptions = data?.product?.info?.totalNumberOfOptions;

  const productHeaderClasses = classNames({
    'sui-mr-8': isMobile,
    'sui-mt-8': isMobile && data?.product?.badges?.length > 0,
  });
  const productPodClasses = classNames('sui-p-1', {
    'sui-h-full': !isMobile,
  });

  useEffect(() => {
    if (!itemId) {
      sendDataToNewRelic({
        appName: 'nucleus.promotions',
        actionName: 'special-buy-promo-itemId-missing',
        dealType: 'sbotd',
        segment: 'Promotions',
        outOfStockItemId: null,
      });
    }
  }, [itemId]);

  return (
    <ProductPod
      itemId={itemId}
      storeId={storeId}
      analyticsData={{ parent: 'special-buy-promos', position }}
      className={productPodClasses}
      render={(pod) => (
        <>
          <PodSticker position="top-left">
            <ProductBadge itemId={pod.itemId} storeId={pod.storeId} />
          </PodSticker>
          {!hideAddToList && (
            <PodSticker position="top-right">
              <PodSpacer padding={['TOP', 'RIGHT']}>
                <AddToList itemId={pod.itemId} showIconButton />
              </PodSpacer>
            </PodSticker>
          )}
          {isMobile ? (
            <>
              <PodSection>
                <PodSpacer>
                  <div className={productHeaderClasses}>
                    <ProductHeader
                      brand="above"
                      itemId={pod.itemId}
                      titleMaxLine={2}
                      brandMaxLine={1}
                      disableShopThisCollection
                    />
                  </div>
                </PodSpacer>
              </PodSection>
              <div className="sui-grid sui-grid-cols-12 sui-gap-2 sui-p-4">
                <div className="sui-col-span-5 sui-border-primary sui-p-0">
                  <ProductImage
                    itemId={pod.itemId}
                    dualImageSwipe
                  />
                  <SeeMoreOptions
                    canonicalUrl={canonicalUrl}
                    totalNumberOfOptions={totalNumberOfOptions}
                    isMobile
                  />
                </div>
                <div className="sui-col-span-7 sui-border-primary sui-p-2">
                  <PodSpacer minHeight="11.2rem" padding={['BOTTOM']}>
                    {showShopThisCollectionLink && (
                      <Link variant="body-xs" href={collectionUrl}>
                        Shop this Collection
                      </Link>
                    )}
                    <ProductRating itemId={pod.itemId} />
                    <ProductServiceAddOns itemId={pod.itemId} isMobile={isMobile} />
                    <Price
                      itemId={pod.itemId}
                      large={false}
                      storeId={pod.storeId}
                      onSavingsCenterToggle={pod.onSavingsCenterToggle}
                      stackWasPrice
                    />
                  </PodSpacer>
                  <PodSpacer minHeight="8rem" padding={['BOTTOM']}>
                    {!hidePickupFulfillment && (
                      <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} />
                    )}
                    <FulfillmentPodShipping itemId={pod.itemId} storeId={storeId} />
                  </PodSpacer>
                  <PodSpacer padding={['TOP']}>
                    <ProductATC
                      doNotShowChooseYourOptions
                      outline
                      itemId={pod.itemId}
                      storeId={pod.storeId}
                    />
                  </PodSpacer>
                </div>
              </div>
            </>
          ) : (
            <>
              <ProductImage
                itemId={pod.itemId}
                showSecondaryImage={pod.showSecondaryImage}
              />
              <PodSpacer padding={['BOTTOM', 'TOP']}>
                <SeeMoreOptions
                  canonicalUrl={canonicalUrl}
                  totalNumberOfOptions={totalNumberOfOptions}
                />
              </PodSpacer>
              <PodSection>
                <ProductHeader
                  brand="above"
                  itemId={pod.itemId}
                  titleMaxLine={2}
                  brandMaxLine={1}
                  disableShopThisCollection
                />
              </PodSection>
              <PodSection columnAlign>
                <ProductRating itemId={pod.itemId} />
                <ProductServiceAddOns itemId={pod.itemId} isMobile={isMobile} />
                <Price
                  itemId={pod.itemId}
                  large={false}
                  storeId={pod.storeId}
                  onSavingsCenterToggle={pod.onSavingsCenterToggle}
                />
              </PodSection>
              <PodSpacer minHeight="8rem" padding={['BOTTOM']}>
                {!hidePickupFulfillment && (
                  <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} />
                )}
                <FulfillmentPodShipping itemId={pod.itemId} storeId={storeId} />
              </PodSpacer>
              <PodFooter>
                <PodSpacer>
                  <ProductATC
                    doNotShowChooseYourOptions
                    outline
                    itemId={pod.itemId}
                    storeId={pod.storeId}
                  />
                </PodSpacer>
              </PodFooter>
            </>
          )}
        </>
      )}
    />
  );
};

SpecialBuyPromosProductPod.displayName = 'SpecialBuyProductPod';

SpecialBuyPromosProductPod.dataModel = extend(
  {
    product: params({
      itemId: stringType().isRequired(),
      storeId: stringType(),
    }).shape({
      dataSources: stringType(),
      itemId: stringType(),
      identifiers: shapeType({
        canonicalUrl: stringType(),
      }),
      info: shapeType({
        totalNumberOfOptions: numberType()
      }),
      badges: arrayOfType(shapeType({})),
    })
  },
  AddToList,
  FulfillmentPodShipping,
  FulfillmentPodStore,
  Price,
  ProductATC,
  ProductBadge,
  ProductHeader,
  ProductImage,
  ProductPod,
  ProductRating,
  ProductServiceAddOns
);

SpecialBuyPromosProductPod.propTypes = {
  itemId: PropTypes.string.isRequired,
  showPlaceholder: PropTypes.bool.isRequired,
  position: PropTypes.number.isRequired,
};

SpecialBuyPromosProductPod.themeProps = {
  hideAddToList: PropTypes.bool,
  hidePickupFulfillment: PropTypes.bool
};

SpecialBuyPromosProductPod.defaultThemeProps = {
  hideAddToList: false,
  hidePickupFulfillment: false
};
